import {
    DATA_ATTR,
    selectors,
    findChosenInsuranceRadio,
} from './shared';

let isAdding = false;

const toggleIsLoading = (node, isLoading) => {
    isAdding = isLoading;
    const loader = document.querySelector(selectors.loader);
    if (loader) {
        loader.dataset.activeTasks = +isLoading;
    }
};

const toggleInsuranceDescriptions = (node) => {
    const insuranceTitles = node.querySelectorAll(`[${DATA_ATTR}-title]`);

    insuranceTitles.forEach((insuranceTitle) => {
        insuranceTitle.addEventListener('click', () => {
            const chosenRadio = findChosenInsuranceRadio(node);
            const hasSelected = chosenRadio && chosenRadio.value !== 'no';
            const insuranceItem = insuranceTitle.closest(selectors.insuranceItem);
            const isCollapsed = insuranceItem.dataset.insuranceItemCollapsed === 'true';

            if (hasSelected) {
                insuranceItem.dataset.insuranceItemCollapsed = !isCollapsed;
            } else {
                node.querySelectorAll(selectors.insuranceItem).forEach((item) => {
                    item.dataset.insuranceItemCollapsed = !isCollapsed;
                });
            }
        });
    });
};

const toggleFormInvalidation = (insuranceNode, isInvalid) => {
    insuranceNode.classList.toggle('is-invalid', isInvalid);
};

const resetInsuranceForm = (insuranceNode, softReset = false) => {
    const inputs = insuranceNode.querySelectorAll(softReset ? 'input[type=checkbox]' : 'input');
    const addToBasketBtn = insuranceNode.querySelector(selectors.modalAddToBasket);
    const consentInputs = insuranceNode.querySelectorAll(selectors.modalConsent);
    const consentErrorBlocks = insuranceNode.querySelectorAll(selectors.helpBlock);

    inputs.forEach((input) => {
        const formGroup = input.closest('.form-group');
        input.checked = false;

        if (!formGroup) {
            return;
        }

        formGroup.classList.remove('is-invalid');
    });

    addToBasketBtn.dataset.disabled = 'disabled';

    insuranceNode.querySelectorAll(selectors.radio)
        .forEach((radioButton) => {
            radioButton.checked = radioButton.getAttribute('data-added') === '1';
        });
    insuranceNode.querySelectorAll(selectors.helpBlock)
        .forEach(helpBlock => helpBlock.style.display = 'none');

    // Sync aria-describedby with consent checkbox state: remove when checked or error hidden, set when unchecked and error visible
    consentInputs.forEach((consentInput, index) => {
        const errorNode = consentErrorBlocks[index];

        if (consentInput.checked || (errorNode && window.getComputedStyle(errorNode).display === 'none')) {
            consentInput.removeAttribute('aria-describedby');
        } else {
            consentInput.setAttribute('aria-describedby', `insurance-consent-error-${index + 1}`);
        }
    });
};

const modalCloseObserver = (insuranceNode, modal) => {
    const observer = new MutationObserver((mutations) => {
        mutations.forEach(mutation =>
            mutation.type === 'attributes' &&
						mutation.attributeName === 'style' &&
						modal.style.display === 'none' &&
						!isAdding &&
						resetInsuranceForm(insuranceNode)
        );
    });

    observer.observe(modal, {
        attributes: true,
        childList: false,
        subtree: false
    });
};

const updateIPIDContext = (input, modal) => {
    const payTodayEl = modal.querySelector(`[${DATA_ATTR}-pay-today]`);
    const payMonthlyEl = modal.querySelector(`[${DATA_ATTR}-pay-monthly]`);
    const insuranceIdEls = modal.querySelectorAll(`[${DATA_ATTR}-id]`);
    const insuranceDocumentLink = modal.querySelector(`[${DATA_ATTR}-document]`);
    const payAnnualEl = modal.querySelector(`[${DATA_ATTR}-pay-annual]`);

    const {
        dataset: {
            insuranceId,
            insurancePrice,
            insuranceAnnualPrice,
            insuranceDuration,
        }
    } = input;

    modal.dataset.insuranceModalContext = insuranceId.toUpperCase();
    modal.dataset.insuranceModalDuration = insuranceDuration.toUpperCase();

    const isMonthly = insuranceDuration.toUpperCase() === 'MONTHLY';

    if (isMonthly) {
        payTodayEl.textContent = `£${insurancePrice}`;
        payMonthlyEl.textContent = `£${insurancePrice}**`;
        payAnnualEl.textContent = `£${insuranceAnnualPrice}`;
    } else {
        payTodayEl.textContent = `£${insurancePrice}`;
        payMonthlyEl.textContent = '£0.00';
    }

    insuranceDocumentLink.href = insuranceDocumentLink.getAttribute(`data-${insuranceId.toLowerCase()}-href`);

    const customTitle = modal.querySelector(selectors.modalTitleSource);
    if (customTitle && customTitle.textContent) {
        modal.querySelector(selectors.modalTitle).textContent = customTitle.textContent;
    }

    if (!insuranceIdEls.length) {
        return;
    }

    insuranceIdEls.forEach(el => el.textContent = insuranceId.toLowerCase());
};

// Add a marker class to the obligations details item for targeted mobile styling.
const markObligationsDetailsItem = (modal) => {
    const detailsItems = modal.querySelectorAll('.insurance-ipid-modal__details-item');

    detailsItems.forEach((item) => {
        item.classList.remove('insurance-ipid-modal__details-item--obligations');
    });

    const obligationsHeading = modal.querySelector('.accessibility-metadata-obligation');
    if (!obligationsHeading) {
        return;
    }

    const obligationsItem = obligationsHeading.closest('.insurance-ipid-modal__details-item');
    if (obligationsItem) {
        obligationsItem.classList.add('insurance-ipid-modal__details-item--obligations');
    }
};

const bindIPIDContext = (insuranceNode, modal) => {
    const inputs = insuranceNode.querySelectorAll('input[type=radio]');
    inputs.forEach(input => input.addEventListener('click', (e) => {
        toggleFormInvalidation(insuranceNode, false);

        if (input.getAttribute('data-added') === '1') {
            e.stopPropagation();
        }

        if (input.value === 'no') {
            handleRemoveInsuranceProduct(insuranceNode, input);
            return;
        }

        const scrollableContainer = insuranceNode.querySelector(selectors.modalScrollableContainer);
        if (scrollableContainer) {
            setTimeout(() => scrollableContainer.scrollTop = 0, 1);
        }

        updateIPIDContext(input, modal);
    }));
};

const bindConsent = (modal) => {
    const consentInputs = modal.querySelectorAll(selectors.modalConsent);
    const addToBasket = modal.querySelector(selectors.modalAddToBasket);
    const onAccept = () => {
        let checked = 0;
        consentInputs.forEach((consentInput) => {
            if (consentInput.checked) {
                checked++;
            }
        });

        checked === consentInputs.length
            ? addToBasket.removeAttribute('data-disabled')
            : addToBasket.dataset.disabled = 'disabled';
    };

    consentInputs.forEach((consentInput) => {
        consentInput.addEventListener('input', () => onAccept());
    });
};

const handleRemoveInsuranceProduct = (insuranceNode, chosenInsuranceRadio) => {
    if (isAdding || chosenInsuranceRadio.dataset.added === '1') {
        return;
    }
    toggleIsLoading(insuranceNode, true);

    const radioButtons = insuranceNode.querySelectorAll('input[type="radio"]');
    // eslint-disable-next-line no-unused-vars
    let insuranceIsAddedToTheBasket = false;
    radioButtons.forEach((radioButton) => {
        radioButton.disabled = true;
        if (radioButton.dataset.added === '1') {
            insuranceIsAddedToTheBasket = true;
        }
    });

    const formData = new URLSearchParams();
    formData.append('publicId', chosenInsuranceRadio.dataset.basketDealLineId);

    let updatedPrices;

    fetch('/basket/mobile-insurance/clear', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData
    })
        .then(response => response.json())
        .then((data) => {
            if (data.shouldReloadPage) {
                return window.location.reload();
            }
            if (data.result === 'success') {
                insuranceNode.querySelectorAll(selectors.radio).forEach(radio => radio.dataset.added = '0');
                chosenInsuranceRadio.setAttribute('data-added', '1');
                updatedPrices = data.prices || null;

                if (data.discountWasRemoved) {
                    showRemoveDiscountPopUp();
                }

            } else {
                throw data;
            }
        })
        .catch(() => {
            chosenInsuranceRadio.checked = false;
        })
        .finally(() => {
            radioButtons.forEach(radioButton => radioButton.disabled = false);
            toggleIsLoading(insuranceNode, false);
            resetInsuranceForm(insuranceNode, true);
            updatedPrices && updatePrices(updatedPrices, 'clear');
        });
};

const handleCollapse = (insuranceNode, radioButtons, chosenInsuranceRadio) => {
    radioButtons.forEach(radioButton => radioButton.setAttribute('data-added', '0'));
    chosenInsuranceRadio.setAttribute('data-added', '1');

    insuranceNode.querySelectorAll(selectors.insuranceItem).forEach((insuranceItem) => {
        insuranceItem.dataset.insuranceItemCollapsed = true;
    });
    const insuranceItem = chosenInsuranceRadio.closest(selectors.insuranceItem);
    insuranceItem.dataset.insuranceItemCollapsed = false;
};

const handleAddToBasket = (insuranceNode, modal) => {
    const addToBasketBtn = insuranceNode.querySelector(`[${DATA_ATTR}-add-to-basket]`);
    const closeModal = modal.querySelector('[data-dismiss=modal]');

    addToBasketBtn.addEventListener('click', () => {
        if (isAdding) {
            return false;
        }

        if (addToBasketBtn.hasAttribute('data-disabled')) {
            const consentInputs = modal.querySelectorAll(selectors.modalConsent);
            const errorMsg = modal.querySelectorAll(selectors.helpBlock);
            // Tech insurance modal: sync error visibility with aria-describedby and refresh error text for screen reader announcement
            consentInputs.forEach((consentInput, index) => {
                errorMsg[index].style.display = consentInput.checked ? 'none' : 'block';

                if (consentInput.checked) {
                    consentInput.removeAttribute('aria-describedby');
                } else {
                    consentInput.setAttribute('aria-describedby', `insurance-consent-error-${index + 1}`);
                }
            });

            modal.querySelector(selectors.modalContent).scrollTo({
                top: modal.scrollHeight,
                left: 0,
                behavior: 'smooth',
            });

            return false;
        }

        const chosenInsuranceRadio = findChosenInsuranceRadio(insuranceNode);
        if (!chosenInsuranceRadio) {
            return;
        }
        toggleIsLoading(insuranceNode, true);
        addToBasketBtn.dataset.disabled = 'disabled';
        addToBasketBtn.dataset.isAdding = true;
        const radioButtons = insuranceNode.querySelectorAll(selectors.radio);
        radioButtons.forEach(radioButton => radioButton.disabled = true);

        const publicId = chosenInsuranceRadio.getAttribute('data-basket-deal-line-id');
        const dataKeys = [
            'insuranceProduct',
            'insuranceBand',
            'insuranceSyscovercode',
            'insuranceFirstPremiumPayment',
            'insuranceTotalAnnualPremium',
            'insuranceDuration',
            'insuranceIsDiscount'
        ];
        const formData = new URLSearchParams();
        formData.append('publicId', publicId);
        dataKeys.forEach((key) => {
            formData.append(
                `form[${publicId}-mobileInsurance][${key}]`,
                chosenInsuranceRadio.dataset[key]
            );
        });

        let updatedPrices, showDiscountModal = false, showRemoveDiscountModal = false;

        fetch('/basket/mobile-insurance/add', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: formData
        })
            .then(response => response.json())
            .then((data) => {
                if (data.shouldReloadPage) {
                    return window.location.reload();
                }
                if (data.result === 'success') {
                    handleCollapse(insuranceNode, radioButtons, chosenInsuranceRadio);
                    updatedPrices = data.prices || null;
                    if (data.discountWasApplied) {
                        showDiscountModal = true;
                    } else if (data.discountWasRemoved) {
                        showRemoveDiscountModal = true;
                    }
                } else {
                    throw data;
                }
            })
            .catch(() => {
                chosenInsuranceRadio.checked = false;
            })
            .finally(() => {
                addToBasketBtn.dataset.isAdding = false;
                radioButtons.forEach(radioButton => radioButton.disabled = false);
                resetInsuranceForm(insuranceNode, true);
                toggleIsLoading(insuranceNode, false);
                closeModal.click();
                updatedPrices && updatePrices(updatedPrices, 'add');
                if (showDiscountModal) {
                    showDiscountPopUp();
                }
                if (showRemoveDiscountModal) {
                    showRemoveDiscountPopUp();
                }
            });
    });
};

const checkValidityOnBasketSubmit = (e) => {
    const insuranceNodes = document.querySelectorAll(selectors.component);
    let yAxisOffset;

    if (insuranceNodes.length) {
        insuranceNodes.forEach((insuranceNode) => {
            const inputs = insuranceNode.querySelectorAll(selectors.radio);
            let isChosen = false;
            let noOptionRadio = null;

            inputs.forEach((input) => {
                if (input.checked) {
                    isChosen = true;
                }
                if (input.value === 'no') {
                    noOptionRadio = input;
                }
            });

            if (!isChosen && noOptionRadio) {
                noOptionRadio.checked = true;
                handleRemoveInsuranceProduct(insuranceNode, noOptionRadio);
                return;
            }

            if (!isChosen) {
                toggleFormInvalidation(insuranceNode, true);
                e.stopPropagation();
                e.preventDefault();
                if (!yAxisOffset) {
                    yAxisOffset = Math.abs(insuranceNode.getBoundingClientRect().y);
                    const topOffset = +Math.abs(window.scrollY - yAxisOffset).toFixed(0);
                    window.scrollTo({
                        top: topOffset,
                        behavior: 'smooth'
                    });
                }
            } else {
                toggleFormInvalidation(insuranceNode, false);
            }
        });
    }
};

const updateDetailContentBlocksHeight = (insuranceNode) => {
    if (window.innerWidth < 1024) {
        return;
    }
    ['.insurance-component__item-inclusions', '.insurance-component__item-exclusions'].forEach((className) => {
        let maxHeight = 0;
        const els = insuranceNode.querySelectorAll(className);
        els.forEach(el => maxHeight = Math.max(maxHeight, el.clientHeight));
        els.forEach(el => el.style.minHeight = `${maxHeight}px`);
    });
};

const showDiscountPopUp = () => {
    document.querySelector('#multi-policy-discount-modal .add-discount').style.display = 'block';
    document.querySelector('#multi-policy-discount-modal .remove-discount').style.display = 'none';
    $('#multi-policy-discount-modal').modal('show');
};

const showRemoveDiscountPopUp = () => {
    document.querySelector('#multi-policy-discount-modal .add-discount').style.display = 'none';
    document.querySelector('#multi-policy-discount-modal .remove-discount').style.display = 'block';
    $('#multi-policy-discount-modal').modal('show');
};
window.showRemoveDiscountPopUp = showRemoveDiscountPopUp;

const updatePrices = (prices, state = 'add') => {
    if (!prices) {
        return;
    }
    if (prices.basketTotals) {

        /* Since the returned value has html in it which the new UI does not use, we are extracting the required values and using them. This would have been easier if the values were returned directly as well, instead of just with HTML. */
        const [updatedMonthlyCost, updatedPayTodayCost] = prices.basketTotals.match(/&pound;([^&\n]+)/g).map(str => str.split('\n')[0]);
        const updatedInsuranceText = (prices.basketTotals.match(/\(([^)]+)\)/) || [])[1] || null;
        const basketTotals = document.querySelectorAll(selectors.basketTotals);
        if (basketTotals) {
            basketTotals.forEach((basketTotalElement) => {
                basketTotalElement.querySelector('[data-basket-total-monthly]').innerHTML = updatedMonthlyCost;
                basketTotalElement.querySelector('[data-basket-total-pay-today]').innerHTML = updatedPayTodayCost;
                if (updatedInsuranceText) {
                    basketTotalElement.querySelector('[data-basket-total-insurance-text] span').innerHTML = updatedInsuranceText;
                    basketTotalElement.querySelector('[data-basket-total-insurance-text]').classList.remove('hidden');
                } else {
                    basketTotalElement.querySelector('[data-basket-total-insurance-text] span').innerHTML = '';
                    basketTotalElement.querySelector('[data-basket-total-insurance-text]').classList.add('hidden');
                }
            });
        }
    }
    if (prices.hasOwnProperty('insurancePrices')) {
        document.querySelectorAll(selectors.insuranceItemPrices).forEach((insurancePriceElement) => {
            insurancePriceElement.innerHTML = prices.insurancePrices[insurancePriceElement.dataset.basketLineId] || '';
            if (state == 'add') {
                insurancePriceElement.classList.remove('hidden');
            } else {
                insurancePriceElement.classList.add('hidden');
            }
        });
    }

    // Custom form is being used and hence this form is not needed
    // if (updateForm && prices.hasOwnProperty('insuranceForms')) {
    //     document.querySelectorAll(selectors.component).forEach((insuranceComponent) => {
    //         const basketDealLineId = insuranceComponent.dataset.basketLineId;
    //         let updatedComponent = prices.insuranceForms[basketDealLineId];
    //         if (updatedComponent) {
    //             insuranceComponent.outerHTML = updatedComponent;
    //             reInitInsuranceNode(basketDealLineId);
    //         }
    //     });
    // }
};

// not needed anymore as form isn't re-rended anymore
// const reInitInsuranceNode = (basketDealLineId) => {
//     const insuranceComponent = document.querySelector(
//         `${selectors.component}[data-basket-line-id="${basketDealLineId}"]`
//     );
//     insuranceComponent && initInsuranceNode(insuranceComponent);
// };

const initInsuranceNode = (insuranceNode) => {
    const modal = insuranceNode.querySelector(selectors.modal);
    toggleInsuranceDescriptions(insuranceNode);
    if (!modal) {
        return;
    }
    markObligationsDetailsItem(modal);
    bindConsent(modal);
    bindIPIDContext(insuranceNode, modal);
    modalCloseObserver(insuranceNode, modal);
    handleAddToBasket(insuranceNode, modal);
    updateDetailContentBlocksHeight(insuranceNode);
};
const init = () => {
    const insuranceNodes = document.querySelectorAll(selectors.component);

    if (!insuranceNodes.length) {
        return;
    }

    const basketFormButton = document
        .querySelector(selectors.basketFormButton);

    if (basketFormButton) {
        basketFormButton.addEventListener('click', e =>
            checkValidityOnBasketSubmit(e));
    }

    insuranceNodes.forEach(initInsuranceNode);
};

export default init;
